import { Box, Breadcrumbs, CircularProgress, Divider, Link, Paper, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { GroupDto } from '../../common/interfaces/kindergarten.interface';
import { GeneralStatusEnum, GroupDetailsTabs } from '../../common/enums/common.enum';
import { fetchGroupDetails } from '../../api/kindergarten.api';
import GroupTeachersTab from './components/GroupTeachersTab';
import GroupEventsTab from './components/GroupEventsTab';
import GroupChildrenTab from './components/GroupChildrenTab';
import { useTranslation } from 'react-i18next';

const GroupDetails = () => {
  const { t } = useTranslation();
  const { groupId, kindergartenId, kindergartenName, locationName } = useParams<{
    groupId: string;
    kindergartenId: string;
    kindergartenName: string;
    locationName: string;
  }>();

  const [group, setGroup] = useState<GroupDto | null>(null);
  const [activeTab, setActiveTab] = useState<GroupDetailsTabs>(GroupDetailsTabs.Teachers);
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    if (groupId && kindergartenId) {
      getGroupDetails(kindergartenId, groupId);
    }
  }, [groupId, kindergartenId]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const getGroupDetails = async (kindergartenId: string, groupId: string) => {
    try {
      if (!kindergartenId) {
        return;
      }
      if (!groupId) {
        return;
      }
      const response = await fetchGroupDetails(kindergartenId, groupId);
      if (!response.results) return;
      setGroup(response.results);
    } catch (error) {}
  };
  if (!group) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const tabs = [
    { label: t('kindergarten.tabs.teachers'), value: GroupDetailsTabs.Teachers },
    { label: t('kindergarten.tabs.children'), value: GroupDetailsTabs.Children },
    {
      label: t('kindergarten.tabs.eventConfiguration'),
      value: GroupDetailsTabs.EventConfiguration
    }
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', p: 7 }}>
      <Breadcrumbs aria-label={t('common.breadcrumb')} sx={{ mb: 2 }}>
        <Link color="text.secondary" underline="hover" onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
          {t('nav.kindergartens')}
        </Link>
        {kindergartenName && kindergartenId && (
          <Link color="text.secondary" underline="hover" onClick={() => navigate(`/${kindergartenId}/details`)} sx={{ cursor: 'pointer' }}>
            {decodeURIComponent(kindergartenName)}
          </Link>
        )}
        <Typography>{group.name}</Typography>
      </Breadcrumbs>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h1">{group.name}</Typography>

        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 400,
            color: 'text.primary',
            fontFamily: 'Poppins',
            lineHeight: 'normal'
          }}
        >
          {decodeURIComponent(locationName ?? '')}
        </Typography>
        {group.description && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            {group.description}
          </Typography>
        )}
      </Box>

      <Box sx={{ mt: 4 }}>
        <Tabs value={activeTab} onChange={handleTabChange} textColor="primary" indicatorColor="primary">
          {tabs.map(({ label, value }) => (
            <Tab
              key={value}
              label={label}
              value={value}
              sx={{
                ...theme.typography.caption,
                textTransform: 'none',
                color: theme => (activeTab === value ? theme.palette.primary.main : theme.palette.text.secondary)
              }}
            />
          ))}
        </Tabs>

        <Divider />

        {groupId && kindergartenId && (
          <Box sx={{ mt: 3 }}>
            {activeTab === GroupDetailsTabs.Teachers && (
              <GroupTeachersTab
                groupId={groupId}
                kindergartenId={kindergartenId}
                isGroupActive={group.status === GeneralStatusEnum.ACTIVE}
              />
            )}
            {activeTab === GroupDetailsTabs.Children && (
              <GroupChildrenTab
                groupId={groupId}
                kindergartenId={kindergartenId}
                isGroupActive={group.status === GeneralStatusEnum.ACTIVE}
              />
            )}
            {activeTab === GroupDetailsTabs.EventConfiguration && (
              <GroupEventsTab groupId={groupId} isGroupActive={group.status === GeneralStatusEnum.ACTIVE} />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default GroupDetails;
